  import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
  import { createPortal } from 'react-dom';
  import { ChevronDown, Plus } from 'lucide-react';

  const ReusableDropdown = ({
    items = [],
    value = '',
    onChange,
    onSelect,
    placeholder = 'Type to search...',
    emptyMessage = 'No results found',
    maxItems = 8,
    getItemKey = (item) => item.id || item.email,
    getItemLabel = (item) => item.name || item.email,
    filterFunction,
    renderItem,
    theme = 'light',
    language = 'en',
    disabled = false,
    inputStyle = {},
    dropdownStyle = {},
    itemStyle = {},
    showAllOnArrowClick = true, // خاصية جديدة
    // ── Creatable support (opt-in) ──────────────────────────────────────
    // When true and the typed search term has no exact (case-insensitive)
    // match among `items`, a synthetic "+ Add ..." row is appended.
    // Selecting it calls `onCreate(searchTerm)`, which must return the
    // created item (or null/undefined to cancel — e.g. on error).
    allowCreate = false,
    onCreate,
    createLabel,
  }) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const [filteredItems, setFilteredItems] = useState([]);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [searchTerm, setSearchTerm] = useState('');
    const [creating, setCreating] = useState(false);
    const [displayedValue, setDisplayedValue] = useState(value ?? ''); // Track displayed value
    const containerRef = useRef(null);
    const portalRef = useRef(null);
    const inputRef = useRef(null);
    const [dropdownCoords, setDropdownCoords] = useState(null);
    const [dropdownDirection, setDropdownDirection] = useState('down');
    const isRTL = language === 'ar';

    // Sync displayedValue when value prop changes
    useEffect(() => {
      setDisplayedValue(value ?? '');
    }, [value]);

    const defaultFilterFunction = (item, searchTerm) => {
      const label = String(getItemLabel(item) || '').toLowerCase();
      const search = String(searchTerm || '').toLowerCase();
      return label.includes(search);
    };

    const withCreateRow = (list) => {
      const trimmed = String(searchTerm || '').trim();
      if (!allowCreate || !trimmed) return list;
      const exists = (items || []).some(
        (item) => String(getItemLabel(item) || '').trim().toLowerCase() === trimmed.toLowerCase()
      );
      if (exists) return list;
      return [...list, { __isCreateNew: true, __searchTerm: trimmed }];
    };

    useEffect(() => {
      // No search term: show the complete list (the dropdown body scrolls)
      if (!String(searchTerm || '').trim()) {
        setFilteredItems(items || []);
        setHighlightedIndex(-1);
        return;
      }

      const filterFunc = filterFunction || defaultFilterFunction;
      const filtered = (items || []).filter(item => filterFunc(item, searchTerm));
      setFilteredItems(withCreateRow(filtered));
      setHighlightedIndex(-1);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm, items, filterFunction, getItemLabel, allowCreate]);

    useEffect(() => {
      const handleClickOutside = (event) => {
        const container = containerRef.current;
        const portal = portalRef.current;
        const clickedInsideContainer = container && container.contains(event.target);
        const clickedInsidePortal = portal && portal.contains(event.target);
        if (!clickedInsideContainer && !clickedInsidePortal) {
          setShowDropdown(false);
          setSearchTerm(''); // Clear search when closing dropdown
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // compute dropdown coordinates for portal rendering to escape stacking contexts
    const updateDropdownCoords = () => {
      const inputEl = inputRef.current;
      if (!inputEl) return;
      const rect = inputEl.getBoundingClientRect();
      // estimate dropdown height to decide whether to open up or down
      const headerHeight = 48; // header area showing results count
      const itemHeight = 44; // approx per-item height
      const maxHeight = parseInt(baseDropdownStyle.maxHeight, 10) || 300;
      const estimatedHeight = Math.min(maxHeight, headerHeight + (filteredItems.length * itemHeight));
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openUp = spaceBelow < estimatedHeight && spaceAbove > spaceBelow;

      setDropdownDirection(openUp ? 'up' : 'down');

      setDropdownCoords({
        // for fixed positioning we use viewport coordinates
        top: openUp ? (rect.top - estimatedHeight) : rect.bottom,
        left: rect.left,
        width: rect.width,
        height: estimatedHeight,
      });
    };

    useLayoutEffect(() => {
      if (!showDropdown) {
        // drop stale coords so a reopen can't flash the menu at an old position
        setDropdownCoords(null);
        return;
      }
      updateDropdownCoords();
      const handler = (e) => {
        // scrolling INSIDE the list must not reposition (it re-renders every frame)
        if (e && e.target && portalRef.current && portalRef.current.contains(e.target)) return;
        updateDropdownCoords();
      };
      window.addEventListener('resize', handler);
      window.addEventListener('scroll', handler, true);
      return () => {
        window.removeEventListener('resize', handler);
        window.removeEventListener('scroll', handler, true);
      };
    }, [showDropdown, filteredItems.length]);

    const handleKeyDown = (e) => {
      if (!showDropdown || filteredItems.length === 0) return;
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex(prev => prev < filteredItems.length - 1 ? prev + 1 : 0);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex(prev => prev > 0 ? prev - 1 : filteredItems.length - 1);
          break;
        case 'Enter':
          e.preventDefault();
          if (highlightedIndex >= 0) handleSelect(filteredItems[highlightedIndex]);
          else if (filteredItems.length === 1) handleSelect(filteredItems[0]);
          break;
        case 'Escape':
          setShowDropdown(false);
          setSearchTerm('');
          break;
        default:
          break;
      }
    };

    const commitSelection = (item) => {
      // Send the item's id to onChange, not the label
      const itemId = getItemKey(item);
      setDisplayedValue(itemId); // Update displayed value immediately
      if (onChange) onChange(itemId);
      if (onSelect) onSelect(item);
      setShowDropdown(false);
      setHighlightedIndex(-1);
      setSearchTerm(''); // Clear search term after selection
      if (inputRef.current) {
        inputRef.current.blur(); // Remove focus from input
      }
    };

    const handleSelect = async (item) => {
      if (item && item.__isCreateNew) {
        if (!onCreate || creating) return;
        setCreating(true);
        try {
          const created = await onCreate(item.__searchTerm);
          if (created) commitSelection(created);
        } finally {
          setCreating(false);
        }
        return;
      }
      commitSelection(item);
    };

    const handleInputChange = (e) => {
      const newValue = e.target.value;
      // Update search term for filtering
      setSearchTerm(newValue);
      setShowDropdown(true);
    };

    const handleInputFocus = () => {
      // open dropdown on focus so user sees suggestions immediately
      setShowDropdown(true);
    };

    // Toggle dropdown open/close when arrow is clicked
    const handleArrowClick = () => {
      if (disabled) return;
      if (showDropdown) {
        setShowDropdown(false);
        setSearchTerm('');
      } else {
        setShowDropdown(true);
        if (inputRef.current) inputRef.current.focus();
      }
    };

    const baseInputStyle = {
      width: '100%',
      padding: '12px 48px 12px 16px', // مساحة للأزرار
      paddingRight: isRTL ? '16px' : '48px',
      paddingLeft: isRTL ? '48px' : '16px',
      borderRadius: '12px',
      border: theme === 'dark' ? '1px solid rgba(148, 163, 184, 0.3)' : '1px solid rgba(148, 163, 184, 0.3)',
      background: 'transparent',
      color: theme === 'dark' ? '#f1f5f9' : '#334155',
      fontSize: '14px',
      fontWeight: '500',
      outline: 'none',
      transition: 'all 0.3s ease',
      textAlign: isRTL ? 'right' : 'left',
      boxSizing: 'border-box',
      ...inputStyle,
    };

    const baseDropdownStyle = {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      background: theme === 'dark' ? '#0f172a' : '#ffffff',
      border: theme === 'dark' ? '1px solid rgba(148, 163, 184, 0.24)' : '1px solid rgba(148, 163, 184, 0.3)',
      boxShadow: theme === 'dark' ? '0 20px 40px rgba(15, 23, 42, 0.35)' : '0 18px 30px rgba(15, 23, 42, 0.08)',
      borderRadius: '12px',
      zIndex: 99999,
      marginTop: '4px',
      maxHeight: '300px',
      overflowY: 'auto',
      direction: isRTL ? 'rtl' : 'ltr',
      ...dropdownStyle,
    };

    const baseItemStyle = {
      padding: '12px 16px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      fontSize: '14px',
      color: theme === 'dark' ? '#f1f5f9' : '#334155',
      borderBottom: theme === 'dark' ? '1px solid rgba(148, 163, 184, 0.1)' : '1px solid #f3f4f6',
      transition: 'all 0.2s ease',
      flexDirection: isRTL ? 'row-reverse' : 'row',
      ...itemStyle,
    };

    const defaultRenderItem = (item) => (
      <div style={{ flex: 1, textAlign: isRTL ? 'right' : 'left' }}>
        <div style={{ fontWeight: 600 }}>{getItemLabel(item)}</div>
      </div>
    );

    // Resolve the label for the current selection. Callers are inconsistent about
    // what `value` holds (item key for object items, the label itself for string
    // items), and ids may be numbers on items but strings from the parent — so
    // compare as strings and fall back from key match to label match to raw value.
    const resolveSelectedLabel = () => {
      const v = displayedValue;
      if (v === '' || v === null || v === undefined) return '';
      const list = items || [];
      const byKey = list.find(item => String(getItemKey(item)) === String(v));
      if (byKey) return getItemLabel(byKey);
      const byLabel = list.find(item => String(getItemLabel(item)) === String(v));
      if (byLabel) return getItemLabel(byLabel);
      // items still loading: don't flash a raw id
      if (list.length === 0) return '';
      return String(v);
    };

    // While open, the input is purely a search box (the selection shows as the
    // placeholder); while closed, it displays the selected label. Mixing the two
    // made typing edit the selected label text and break filtering.
    const getDisplayText = () => (showDropdown ? searchTerm : resolveSelectedLabel());

    return (
      <div ref={containerRef} data-reusable-dropdown="true" style={{ position: 'relative', width: '100%' }}>
        <div style={{ position: 'relative' }}>
          <input
            ref={inputRef}
            type="text"
            value={getDisplayText()}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            placeholder={showDropdown ? (resolveSelectedLabel() || placeholder) : placeholder}
            disabled={disabled}
            autoComplete="off"
            style={baseInputStyle}
          />

          <div style={{
            position: 'absolute',
            left: isRTL ? '8px' : 'auto',
            right: isRTL ? 'auto' : '8px',
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            gap: '4px',
            alignItems: 'center',
          }}>
            {showAllOnArrowClick && (
              <button
                type="button"
                onClick={handleArrowClick}
                disabled={disabled}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  color: theme === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(26,26,26,0.6)',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                  transform: showDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
                onMouseEnter={(e) => !disabled && (e.currentTarget.style.color = theme === 'dark' ? 'rgba(255,255,255,0.9)' : 'rgba(26,26,26,0.9)')}
                onMouseLeave={(e) => e.currentTarget.style.color = theme === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(26,26,26,0.6)'}
              >
                <ChevronDown size={18} />
              </button>
            )}
          </div>
        </div>

        {showDropdown && filteredItems.length > 0 && (
          // render dropdown in a portal so it escapes parent stacking contexts
          (typeof document !== 'undefined' && dropdownCoords)
            ? createPortal(
              <div
                ref={portalRef}
                className="reusable-dropdown-portal"
                style={{
                  position: 'fixed',
                  top: dropdownCoords.top + 'px',
                  left: dropdownCoords.left + 'px',
                  width: dropdownCoords.width + 'px',
                  background: baseDropdownStyle.background,
                  border: baseDropdownStyle.border,
                  borderRadius: baseDropdownStyle.borderRadius,
                  boxShadow: baseDropdownStyle.boxShadow,
                  zIndex: baseDropdownStyle.zIndex || 99999,
                  marginTop: dropdownDirection === 'down' ? '4px' : 0,
                  maxHeight: baseDropdownStyle.maxHeight,
                  overflowY: baseDropdownStyle.overflowY,
                  direction: baseDropdownStyle.direction,
                }}
              >
                <div style={{
                  padding: '12px 16px',
                  borderBottom: theme === 'dark' ? '1px solid rgba(148, 163, 184, 0.1)' : '1px solid #f3f4f6',
                  fontSize: '12px',
                  color: theme === 'dark' ? 'rgba(203, 213, 225, 0.6)' : '#6b7280',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  textAlign: isRTL ? 'right' : 'left',
                }}>
                  {filteredItems.length} {language === 'ar' ? 'نتيجة' : 'results'}
                </div>

                {filteredItems.map((item, index) => {
                  const isHighlighted = index === highlightedIndex;
                  const isCreate = item && item.__isCreateNew;
                  const itemKey = isCreate ? '__create_new__' : getItemKey(item);
                  return (
                    <div
                      key={itemKey}
                      style={{
                        ...baseItemStyle,
                        background: isHighlighted ? (theme === 'dark' ? 'rgba(59, 130, 246, 0.12)' : 'rgba(59, 130, 246, 0.08)') : 'transparent',
                        opacity: isCreate && creating ? 0.6 : 1,
                        cursor: isCreate && creating ? 'wait' : 'pointer',
                        color: isCreate ? '#2563eb' : baseItemStyle.color,
                        fontWeight: isCreate ? 600 : baseItemStyle.fontWeight,
                      }}
                      onMouseDown={(e) => {
                        // select on mousedown (with preventDefault) so the input's
                        // blur/reposition can't swallow the first click
                        e.preventDefault();
                        handleSelect(item);
                      }}
                      onMouseEnter={() => setHighlightedIndex(index)}
                    >
                      {isCreate ? (
                        <>
                          <Plus size={14} />
                          <span>
                            {createLabel
                              ? createLabel(item.__searchTerm)
                              : (language === 'ar' ? `إضافة "${item.__searchTerm}"` : `Add "${item.__searchTerm}"`)}
                          </span>
                        </>
                      ) : (
                        renderItem ? renderItem(item, theme, language) : defaultRenderItem(item)
                      )}
                    </div>
                  );
                })}
              </div>,
              document.body
            )
            : null
        )}

        {showDropdown && String(searchTerm || '').trim() && filteredItems.length === 0 && (
          <div className="reusable-dropdown-portal" style={baseDropdownStyle}>
            <div style={{ padding: '20px 16px', textAlign: 'center', color: theme === 'dark' ? 'rgba(203, 213, 225, 0.5)' : '#6b7280', fontSize: '14px' }}>{emptyMessage}</div>
          </div>
        )}

        <style>{`
          .reusable-dropdown-portal {
            scrollbar-width: thin;
            scrollbar-color: ${theme === 'dark' ? 'rgba(148, 163, 184, 0.55) rgba(15, 23, 42, 0.85)' : 'rgba(148, 163, 184, 0.45) rgba(255, 255, 255, 0.95)'};
          }
          .reusable-dropdown-portal::-webkit-scrollbar { width: 10px; }
          .reusable-dropdown-portal::-webkit-scrollbar-track { background: ${theme === 'dark' ? 'rgba(148, 163, 184, 0.08)' : 'rgba(226, 232, 240, 0.75)'}; border-radius: 999px; }
          .reusable-dropdown-portal::-webkit-scrollbar-thumb { background: ${theme === 'dark' ? 'rgba(148, 163, 184, 0.5)' : 'rgba(148, 163, 184, 0.55)'}; border-radius: 999px; border: 2px solid transparent; background-clip: content-box; }
          .reusable-dropdown-portal::-webkit-scrollbar-thumb:hover { background: ${theme === 'dark' ? 'rgba(148, 163, 184, 0.75)' : 'rgba(148, 163, 184, 0.75)'}; }
        `}</style>
      </div>
    );
  };

  // Named export: unified control style for inputs to match dropdown appearance
  export const unifiedControlStyle = (theme, isRTL = false) => ({
    width: '100%',
    padding: '12px 16px',
    borderRadius: '12px',
    border: theme === 'dark' ? '1px solid rgba(148, 163, 184, 0.2)' : '1px solid rgba(15, 23, 42, 0.1)',
    background: theme === 'dark' ? 'rgba(15, 23, 42, 0.5)' : 'rgba(248, 250, 252, 0.8)',
    color: theme === 'dark' ? '#f1f5f9' : '#1e293b',
    fontSize: '14px',
    fontWeight: '500',
    outline: 'none',
    transition: 'all 0.3s ease',
    textAlign: isRTL ? 'right' : 'left',
    boxSizing: 'border-box',
  });

  export default ReusableDropdown;